import { type CSSProperties, type ReactNode } from 'react';
import { type RequestContext } from '@cx-web/domain-types';

import { type BettingInsightsContent, type EsIndex } from '../types';

// Type for the context that will be passed as first parameter to render functions
export interface BettingInsightsTableHeadingContext {
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  repubConfig: RequestContext['repubConfig'];
  user: RequestContext['user'];
}

export interface BettingInsightsTableHeadingConfig<TIndex extends EsIndex> {
  className?: string;
  highlight?: boolean;
  isMain?: boolean;
  justifyContent?: CSSProperties['justifyContent'];
  key: string;
  mobileView?: 'hide';
  render: (
    context: BettingInsightsTableHeadingContext,
    data: { content: BettingInsightsContent<TIndex>; resultIndex: number },
  ) => ReactNode;
  renderMobile?: (
    context: BettingInsightsTableHeadingContext,
    data: { content: BettingInsightsContent<TIndex>; resultIndex: number },
  ) => ReactNode;
  shouldRenderMobile?: boolean;
  shouldShowIndicator?: boolean;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  tooltipText?: string;
}

// Type alias for the return type of createBettingInsightsTableHeading
export type BettingInsightsTableHeading<TIndex extends EsIndex> = Readonly<{
  className: string;
  highlight: boolean;
  isMain: boolean;
  justifyContent?: CSSProperties['justifyContent'];
  key: string;
  mobileView?: 'hide';
  render: (data: { content: BettingInsightsContent<TIndex>; resultIndex: number }) => ReactNode;
  renderMobile?: (data: { content: BettingInsightsContent<TIndex>; resultIndex: number }) => ReactNode;
  shouldRenderMobile: boolean;
  shouldShowIndicator: boolean;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  tooltipText?: string;
}>;

/**
 * Factory function to create BettingInsightsTableHeading configuration (functional approach)
 * @param requestContext - The request context containing environment, feature flags, repub config, and user details
 * @param config - The table heading configuration with render function and optional properties
 * @returns Immutable table heading configuration object with bound render methods
 * @example
 * ```typescript
 * const heading = createBettingInsightsTableHeading(requestContext, {
 *   key: 'team',
 *   render(context, { content, resultIndex }) {
 *     // context has access to environment, featureFlags, repubConfig, user
 *     return <span>{content.teamName}</span>;
 *   },
 *   renderMobile(context, { content, resultIndex }) {
 *     return <span className="mobile">{content.teamName}</span>;
 *   },
 *   isMain: true,
 *   shouldShowIndicator: true,
 *   tooltipText: 'Team information'
 * });
 * ```
 */
export function createBettingInsightsTableHeading<TIndex extends EsIndex>(
  requestContext: RequestContext,
  config: BettingInsightsTableHeadingConfig<TIndex>,
): BettingInsightsTableHeading<TIndex> {
  // Create context object
  const context: BettingInsightsTableHeadingContext = {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
  };

  // Create the result object with wrapper functions that inject context
  const result = {
    // Config properties
    highlight: config.highlight ?? false,
    isMain: config.isMain ?? false,
    className: config.className ?? '',
    justifyContent: config.justifyContent,
    key: config.key,
    mobileView: config.mobileView,
    shouldRenderMobile: config.shouldRenderMobile ?? false,
    shouldShowIndicator: config.shouldShowIndicator ?? false,
    tooltipPosition: config.tooltipPosition,
    tooltipText: config.tooltipText,

    // Wrapper functions that inject context as first parameter
    render: (data: { content: BettingInsightsContent<TIndex>; resultIndex: number }) => config.render(context, data),
    renderMobile: config.renderMobile
      ? (data: { content: BettingInsightsContent<TIndex>; resultIndex: number }) => config.renderMobile!(context, data)
      : undefined,
  };

  return Object.freeze(result);
}
